import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { Button, Typography } from "Atoms";
import {  useNavigate } from "react-router-dom";
import { cloneTestPlanAPI, getTestPlanByNodeIdAPI, updateTestPlanAPI } from "Zustand/services/projectsService";

type PlanStatus = 0 | 1 | 2; // 0=in progress, 1=approved, 2=rejected

// const STATUS_LABEL: Record<PlanStatus, string> = {
//   0: "In progress",
//   1: "Approved",
//   2: "Rejected",
// };

export default function TestPlanDetail(props:any) {

  const nav = useNavigate();
  const testPlanNodeId = props.testplannodeid ?? "";      // required
  const projectNodeId  = props?.projectnodeid  ?? "";      // needed for cloning payload

  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState<any | null>(null);       // { nodeid, phase, version, status, ... }
  const [status, setStatus] = useState<PlanStatus | "">("");

  // New Version dialog
  const [cloneOpen, setCloneOpen] = useState(false);
  const [phaseInput, setPhaseInput] = useState("");
  const [versionInput, setVersionInput] = useState("");

  /** Load plan on mount */
  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!testPlanNodeId) return;
      setLoading(true);
      try {
        const res = await getTestPlanByNodeIdAPI(testPlanNodeId);
        const testplan = res?.testplan ?? res; // handle both shapes
        if (!active) return;
        setPlan(testplan);
        setStatus((testplan?.status ?? 0) as PlanStatus);
      } finally {
        setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [testPlanNodeId]);

  /** Status change → PUT update */
  const handleStatusChange = async (newStatus: PlanStatus) => {
    setStatus(newStatus);
    try {
      await updateTestPlanAPI({ nodeid: testPlanNodeId, status: newStatus });
      setPlan((p: any) => (p ? { ...p, status: newStatus } : p));
    } catch (e) {
      // revert on error
      setStatus(plan?.status ?? 0);
      console.error("Failed to update plan status", e);
    }
  };

  /** Clone modal confirm */
  const handleCloneConfirm = async () => {
    try {
      await cloneTestPlanAPI(testPlanNodeId, {
        phase: phaseInput.trim(),
        version: versionInput.trim(),
        istestplanofnodeid: projectNodeId,
      });
      // Go back to selector page
      nav("/projects");
    } catch (e) {
      console.error("Clone failed", e);
    }
  };

  const phase = plan?.phase ?? "";
  const version = plan?.version ?? "";

  return (
    <Box>
      <Paper sx={{ p: 3 }} elevation={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5">
            {loading ? "Loading test plan…" : plan?.name || "Test Plan"}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid size={8}>
            <Paper variant="outlined" sx={{ p: 3, minHeight: 320 }}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Typography variant="subtitle2">Phase</Typography>
                  <Typography variant="body1">{phase || "—"}</Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="subtitle2">Version</Typography>
                  <Typography variant="body1">{version || "—"}</Typography>
                </Grid>

                <Grid size={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="plan-status-label">Status</InputLabel>
                    <Select
                      labelId="plan-status-label"
                      label="Status"
                      value={status === "" ? "" : status}
                      onChange={(e) => handleStatusChange(e.target.value as PlanStatus)}
                    >
                      <MenuItem value={0}>In progress</MenuItem>
                      <MenuItem value={1}>Approved</MenuItem>
                      <MenuItem value={2}>Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="flex-end" sx={{ marginTop:"20%"}}>
                <Button variant="outlined" onClick={() => setCloneOpen(true)}>
                  New Version
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* ───────────────── Clone dialog ───────────────── */}
      <Dialog open={cloneOpen} onClose={() => setCloneOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create new version</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Phase"
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            value={phaseInput}
            onChange={(e) => setPhaseInput(e.target.value)}
          />
          <TextField
            label="Version"
            fullWidth
            value={versionInput}
            onChange={(e) => setVersionInput(e.target.value)}
            placeholder="e.g. 1.0.1"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setCloneOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCloneConfirm}
            disabled={!phaseInput.trim() || !versionInput.trim() || !projectNodeId}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
